import { useState } from 'react';
import { Send, FileDown, FileSpreadsheet, Camera } from 'lucide-react';
import { useSubmissionStore } from '@/stores/submissionStore';
import { useUIStore } from '@/stores/uiStore';
import { useBrand } from '@/branding/BrandContext';
import { useTranslation } from '@/lib/i18n';
import { SectionCard } from './SectionCard';
import { FieldLabel } from '../ui/FieldLabel';
import { Cta } from '../ui/Cta';

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function fileBaseName(submission: { id: string; meta: { client: string } }): string {
  const client = (submission.meta.client || 'soumission').replace(/[^a-zA-Z0-9_-]/g, '_');
  const datePart = new Date().toISOString().slice(0, 10);
  const idShort = submission.id.slice(0, 8);
  return `Soumission_${client}_${datePart}_${idShort}`;
}

export function Section05Client() {
  const submission = useSubmissionStore((s) => s.submission);
  const quote = useSubmissionStore((s) => s.quote);
  const catalog = useSubmissionStore((s) => s.catalog);
  const patchMeta = useSubmissionStore((s) => s.patchMeta);
  const showToast = useUIStore((s) => s.showToast);
  const brand = useBrand();
  const { t } = useTranslation();
  const [hasPhoto, setHasPhoto] = useState(false);
  const [busy, setBusy] = useState<null | 'email' | 'pdf' | 'excel'>(null);

  const baseName = fileBaseName(submission);
  const usageLabel =
    submission.config.usage === 'res' ? t('client.usage.res') : t('client.usage.com');

  const handlePdf = async () => {
    if (!quote) {
      showToast(t('toast.no_quote'));
      return;
    }
    setBusy('pdf');
    try {
      const { renderPdf } = await import('@gorampe/exporters');
      const blob = await renderPdf({
        submission,
        quote,
        brand: { name: brand.name, tagline: brand.pdfHeaderSubtitle },
      });
      downloadBlob(blob, `${baseName}.pdf`);
      showToast(t('toast.pdf_done'));
    } catch (err) {
      console.error(err);
      showToast(t('toast.error_pdf'));
    } finally {
      setBusy(null);
    }
  };

  const handleExcel = async () => {
    if (!quote) {
      showToast(t('toast.no_quote'));
      return;
    }
    setBusy('excel');
    try {
      const { renderExcel } = await import('@gorampe/exporters');
      const blob = await renderExcel({
        submission,
        quote,
        ...(catalog ? { catalog } : {}),
      });
      downloadBlob(blob, `${baseName}.xlsx`);
      showToast(t('toast.excel_done'));
    } catch (err) {
      console.error(err);
      showToast(t('toast.error_excel'));
    } finally {
      setBusy(null);
    }
  };

  const handleEmailSend = async () => {
    if (!quote) {
      showToast(t('toast.no_quote'));
      return;
    }
    setBusy('email');
    try {
      const { renderPdf, renderExcel, shareSubmission } = await import('@gorampe/exporters');
      const [pdfBlob, xlsxBlob] = await Promise.all([
        renderPdf({
          submission,
          quote,
          brand: { name: brand.name, tagline: brand.pdfHeaderSubtitle },
        }),
        renderExcel({ submission, quote, ...(catalog ? { catalog } : {}) }),
      ]);
      const subject = `${brand.shortName} — ${submission.meta.client || '...'} ${submission.config.heightIn} po`;
      const body = [
        t('email.body.greeting', { name: submission.meta.client || '' }),
        '',
        t('email.body.intro'),
        '',
        t('email.body.signoff'),
        brand.name,
      ].join('\n');
      const result = await shareSubmission({
        pdfBlob,
        xlsxBlob,
        subject,
        body,
        pdfFilename: `${baseName}.pdf`,
        xlsxFilename: `${baseName}.xlsx`,
        ...(submission.meta.clientEmail ? { to: submission.meta.clientEmail } : {}),
      });
      if (result.method === 'web-share') showToast(t('toast.email_shared'));
      else if (result.method === 'mailto-fallback') showToast(t('toast.email_mailto'));
      else if (result.method === 'failed') showToast(t('toast.email_cancelled'));
    } catch (err) {
      console.error(err);
      showToast(t('toast.error_email'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <SectionCard num="05" title={t('section.05.title')} icon={<Send size={18} strokeWidth={2} />}>
      <p className="text-xs text-[var(--t2,rgba(15,30,51,0.66))] mb-3">
        {t('client.subtitle', { usage: usageLabel, height: submission.config.heightIn })}
      </p>

      <FieldLabel htmlFor="client">{t('client.name.label')}</FieldLabel>
      <input
        id="client"
        type="text"
        value={submission.meta.client}
        onChange={(e) => patchMeta({ client: e.target.value })}
        placeholder={t('client.name.placeholder')}
        className="w-full mb-3 px-3 py-2.5 rounded-xl border border-[var(--line-strong,rgba(15,30,51,0.16))] bg-white text-sm"
      />

      <FieldLabel htmlFor="address">{t('client.address.label')}</FieldLabel>
      <input
        id="address"
        type="text"
        value={submission.meta.siteAddress ?? ''}
        onChange={(e) => patchMeta({ siteAddress: e.target.value })}
        placeholder={t('client.address.placeholder')}
        className="w-full mb-3 px-3 py-2.5 rounded-xl border border-[var(--line-strong,rgba(15,30,51,0.16))] bg-white text-sm"
      />

      <FieldLabel htmlFor="email">{t('client.email.label')}</FieldLabel>
      <input
        id="email"
        type="email"
        inputMode="email"
        value={submission.meta.clientEmail ?? ''}
        onChange={(e) => patchMeta({ clientEmail: e.target.value })}
        placeholder={t('client.email.placeholder')}
        className="w-full mb-3 px-3 py-2.5 rounded-xl border border-[var(--line-strong,rgba(15,30,51,0.16))] bg-white text-sm"
      />

      <FieldLabel htmlFor="photo" icon={<Camera size={12} strokeWidth={2} />}>
        {t('client.photo.label')}
      </FieldLabel>
      <label
        htmlFor="photo"
        className="flex items-center justify-center min-h-[44px] w-full px-3 py-2.5 rounded-xl border-2 border-dashed border-[var(--line-strong,rgba(15,30,51,0.16))] bg-white text-[var(--t2,rgba(15,30,51,0.66))] text-xs cursor-pointer mb-4"
      >
        <Camera size={14} strokeWidth={2} className="mr-2" />
        {hasPhoto ? t('client.photo.added') : t('client.photo.placeholder')}
        <input
          id="photo"
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          onChange={(e) => setHasPhoto(Boolean(e.target.files && e.target.files.length > 0))}
        />
      </label>

      <div className="space-y-2">
        <Cta
          onClick={handleEmailSend}
          disabled={busy !== null}
          icon={<Send size={14} strokeWidth={2.4} />}
        >
          {busy === 'email' ? t('cta.send_email.busy') : t('cta.send_email')}
        </Cta>
        <Cta
          variant="ghost"
          onClick={handlePdf}
          disabled={busy !== null}
          icon={<FileDown size={14} strokeWidth={2} />}
        >
          {busy === 'pdf' ? t('cta.download_pdf.busy') : t('cta.download_pdf')}
        </Cta>
        <Cta
          variant="ghost"
          onClick={handleExcel}
          disabled={busy !== null}
          icon={<FileSpreadsheet size={14} strokeWidth={2} />}
        >
          {busy === 'excel' ? t('cta.download_excel.busy') : t('cta.download_excel')}
        </Cta>
      </div>
    </SectionCard>
  );
}
